import { useEffect, useState } from "react";
import { format } from "date-fns/format";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { CALCULATOR_TYPES } from "constants/calculator";

import * as api from "shared/api/methods";

import { isAuthenticatedSelector, userSelector } from "store/auth/selectors";

import { useWidgetOptions } from "context/widgetOptions";
import { useCommon } from "context/common";

import downloadFile from "helpers/downloadFile";

import { useModal } from "hooks/useModal";
import { useFetch } from "hooks/useFetch";

import { CalculatorProModal } from "components/CalculatorProModal";
import { CalculatorModal, InfoModal } from "components/Modals";

import CalculatorWidget from "./CalculatorWidget";

import {
  AnalyticsType,
  ActivePosition,
  modalTypes,
  calculatorTypes,
} from "./CalculatorWidget.types";

const CalculatorWidgetContainer = ({
  formClassName,
  analyticsClassName,
  initialType,
  widgetId,
  calculationCallBack,
}: {
  formClassName?: string;
  analyticsClassName?: string;
  initialType: calculatorTypes;
  widgetId?: string;
  calculationCallBack?: () => void;
}) => {
  const { t } = useTranslation();
  const { removeParam } = useCommon();
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const user = useSelector(userSelector);
  const [domainCheck, setDomainCheck] = useState<boolean>(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [energyEfficiencyClass, setEnergyEfficiencyClass] = useState("");
  const [proCalculationCharts, setProCalculationCharts] = useState({});
  const [proCalculationDocument, setProCalculationDocument] = useState(null);
  const [proCalculation, setProCalculation] = useState(null);
  const [documentDownloadLoading, setDocumentDownloadLoading] = useState(false);
  const [activePosition, setActivePosition] = useState<ActivePosition>(
    ActivePosition.Ten,
  );
  const [analyticsType, setAnalyticsType] = useState<AnalyticsType>(
    AnalyticsType.General,
  );
  const [modalOpenType, setModalOpenType] = useState<modalTypes | null>(null);
  const [defaultValues, setDefaultValues] = useState(null);

  const [calculatorModalOpen, openCalculatorModal, closeCalculatorModal] =
    useModal({ defaultOpen: false });

  const [infoModalOpen, openInfoModal, closeInfoModal, infoModalData] =
    useModal({ defaultOpen: false });

  const { theme } = useWidgetOptions();

  useEffect(() => {
    const checkDomain = async () => {
      try {
        const response = await api.checkCalculatorDomain();

        setDomainCheck(response.code === "DOMAIN_OK");
      } catch (error) {
        console.warn("Domain check failed:", error);
      }
    };

    checkDomain();
  }, []);

  const { data: lastCalculatorHistory, loading: lastCalculatorHistoryLoading } =
    useFetch(
      {
        defaultData: null,
        fetcher: api.getLastCalculatorHistoryByUserId,
        stopRequest: isAuthenticated ? false : true,
      },
      user?.id,
      [],
    );

  useEffect(() => {
    return () => {
      removeParam("calculation");
    };
  }, []);

  const handlePositionChange = (position: ActivePosition) => {
    setActivePosition(position);
  };

  const handleAnalyticsTypeChange = async () => {
    if (initialType === CALCULATOR_TYPES.BASIC || initialType === undefined) {
      setModalOpenType("details");
      return;
    }

    if (defaultValues && !proCalculationDocument) {
      const document = await api.getProCalculationDocument(defaultValues);

      setProCalculationDocument(document);
    }

    setAnalyticsType((prev) =>
      prev === AnalyticsType.General
        ? AnalyticsType.Details
        : AnalyticsType.General,
    );
  };

  const handleCalculationClick = async (data: any, type?: calculatorTypes) => {
    setSubmitLoading(true);

    try {
      const calculation = await api.getProCalculation({
        ...data,
        calculatorType: type,
        widgetId: widgetId || null,
        organizationId: theme?.organizationId || null,
      });

      const charts = await api.getProCalculationCharts({
        ...data,
        widgetId: widgetId || null,
        organizationId: theme?.organizationId || null,
      });

      setDefaultValues(data);
      setProCalculation(calculation);
      setProCalculationCharts(charts);
      setEnergyEfficiencyClass(data.energyEfficiencyClass);

      if (
        !calculation.baseCalculation.isSonderAfaByDinSize &&
        calculation.baseCalculation.isSonderAfaByEnergyEfficiencyClass
      ) {
        openInfoModal({
          text: t(
            "calculator_pro_page.analytics.info_modal_din_size_notification.text",
          ),
          caption: t(
            "calculator_pro_page.analytics.info_modal_din_size_notification.caption",
          ),
          confirmText: t(
            "calculator_pro_page.analytics.info_modal_din_size_notification.confirm_text",
          ),
          onConfirmClick: () => {
            closeInfoModal();
          },
        });
      }

      if (
        !calculation.baseCalculation.isSonderAfaByFinishDate &&
        calculation.baseCalculation.isSonderAfaForcedByUser
      ) {
        openInfoModal({
          text: t(
            "calculator_pro_page.analytics.info_modal_finish_date_notification.text",
          ),
          caption: t(
            "calculator_pro_page.analytics.info_modal_finish_date_notification.caption",
          ),
          confirmText: t(
            "calculator_pro_page.analytics.info_modal_finish_date_notification.confirm_text",
          ),
          onConfirmClick: () => {
            closeInfoModal();
          },
        });
      }
    } catch (error) {
      console.warn(error);
    } finally {
      closeCalculatorModal();
      setSubmitLoading(false);
      if (calculationCallBack) {
        calculationCallBack();
      }
    }
  };

  const handleDownloadPDFClick = async () => {
    if (initialType === CALCULATOR_TYPES.BASIC || initialType === undefined) {
      setModalOpenType("pdf");
      return;
    }
    setDocumentDownloadLoading(true);
    try {
      const document = await api.downloadProCalculationDocument(defaultValues);

      downloadFile(
        document.url,
        `calculation_document_${format(new Date(), "yyyy-MM-dd")}.pdf`,
      );
    } catch (error) {
      console.error(error);
    } finally {
      setDocumentDownloadLoading(false);
    }
  };

  const handleCalculatorStepChange = () => {
    openCalculatorModal();

    window.scrollTo(0, 0);
  };

  const handleModalsClose = () => {
    setModalOpenType(null);
  };

  const loading = lastCalculatorHistoryLoading;

  return (
    <>
      <CalculatorWidget
        loading={loading}
        formClassName={formClassName}
        analyticsClassName={analyticsClassName}
        documentDownloadLoading={documentDownloadLoading}
        domainCheck={domainCheck}
        isAuthenticated={isAuthenticated}
        submitLoading={submitLoading}
        activePosition={activePosition}
        analyticsType={analyticsType}
        energyEfficiencyClass={energyEfficiencyClass}
        proCalculation={proCalculation}
        proCalculationCharts={proCalculationCharts}
        calculation={proCalculation}
        lastCalculatorHistory={lastCalculatorHistory}
        proCalculationDocument={proCalculationDocument}
        initialType={initialType}
        onPositionChange={handlePositionChange}
        onCalculationClick={handleCalculationClick}
        onDownloadPDFClick={handleDownloadPDFClick}
        onAnalyticsTypeChange={handleAnalyticsTypeChange}
        onCalculatorStepChange={handleCalculatorStepChange}
      />
      <CalculatorModal
        open={calculatorModalOpen}
        submitLoading={submitLoading}
        isAuthenticated={isAuthenticated}
        variant={CALCULATOR_TYPES.STANDARD}
        defaultValues={defaultValues}
        onCalculationClick={handleCalculationClick}
        onClose={closeCalculatorModal}
      />
      <InfoModal
        open={infoModalOpen}
        onClose={closeInfoModal}
        {...infoModalData}
      />
      {modalOpenType && (
        <CalculatorProModal
          modalOpenType={modalOpenType}
          onClose={handleModalsClose}
        />
      )}
    </>
  );
};

export default CalculatorWidgetContainer;
